import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Heading, Text, Icon, Button, Checkbox, Stack } from '@chakra-ui/react';
import { FiCheckSquare, FiPause, FiPlay } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import useAuth from 'hooks/useAuth';
import { getAiSummary, pauseRelease, resumeRelease, resumeSubjects } from 'api/administrador/monitoring/aiGrading';
import ConfirmActionModal from 'components/monitoring/ai/ConfirmActionModal';
import SittingsTab from 'components/monitoring/ai/SittingsTab';
import RunsTab from 'components/monitoring/ai/RunsTab';
import ActivationTab from 'components/monitoring/ai/ActivationTab';
import TestsTab from 'components/monitoring/ai/TestsTab';
import PrechecksTab from 'components/monitoring/ai/PrechecksTab';
import TestResult from 'components/monitoring/ai/TestResult';
import SittingDetailDrawer from 'components/monitoring/ai/SittingDetailDrawer';

dayjs.extend(relativeTime);
dayjs.locale('es');

const ADMIN_ROLES = ['admin', 'supervisor_admin'];
const REFRESH_MS = 30000;

const TABS = [
  { key: 'entregas', label: 'Entregas' },
  { key: 'historial', label: 'Historial' },
  { key: 'revision', label: 'Revisión previa' },
  { key: 'activacion', label: 'Activación' },
  { key: 'pruebas', label: 'Pruebas' },
];

function Chip({ children }) {
  return (
    <Flex align="center" gap={2} bg="rgba(255,255,255,.14)" px={3} py={2} borderRadius="10px" fontSize="12px" whiteSpace="nowrap">
      {children}
    </Flex>
  );
}

function QueueStatus({ queue, onPausedSubjects }) {
  if (!queue) return null;
  // La calificación corre cada 10 min: si pasaron más de 20 min sin ejecución, algo está detenido.
  const last = queue.lastGradeRunAt ? dayjs(queue.lastGradeRunAt) : null;
  const alive = last && dayjs().diff(last, 'minute') <= 20;
  const next = queue.nextRelease?.at ? dayjs(queue.nextRelease.at) : null;
  return (
    <Flex gap={2.5} wrap="wrap" justify="flex-end">
      <Chip>
        <Box w="8px" h="8px" borderRadius="999px" bg={alive ? '#4ADE80' : '#F87171'} />
        {last ? `${alive ? 'Calificando' : 'Detenida'} · última ejecución ${last.fromNow()}` : 'Aún no hay ejecuciones'}
      </Chip>
      {queue.pausedSubjects?.length > 0 && (
        <Box as="button" type="button" onClick={onPausedSubjects} textAlign="left">
          <Chip>
            <Box w="8px" h="8px" borderRadius="999px" bg="#FBBF24" />
            {queue.pausedSubjects.length === 1 ? '1 materia en pausa' : `${queue.pausedSubjects.length} materias en pausa`}
          </Chip>
        </Box>
      )}
      {queue.releasePaused ? (
        <Chip>
          <Box w="8px" h="8px" borderRadius="999px" bg="#FBBF24" />
          Publicación en pausa{queue.releasePausedByEnv ? ' (variable de entorno)' : ''}
        </Chip>
      ) : (
        next && (
          <Chip>
            Próxima publicación {next.isSame(dayjs(), 'day') ? next.format('HH:mm') : `mañana ${next.format('HH:mm')}`} · {queue.nextRelease.count} entregas
          </Chip>
        )
      )}
    </Flex>
  );
}

export default function MonitoringCalificacionActividades() {
  const { user } = useAuth();
  const isAllowed = ADMIN_ROLES.includes(user?.role?.type);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'entregas';
  const [queue, setQueue] = useState(null);
  const [tick, setTick] = useState(0);
  const [openSitting, setOpenSitting] = useState(null);
  const [modal, setModal] = useState(null);
  const [subjectsToResume, setSubjectsToResume] = useState([]);

  const loadQueue = useCallback(async () => {
    try {
      const res = await getAiSummary({ period: 'today' });
      setQueue(res?.queue || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[calificación] error cargando el estado de la cola:', e);
    }
  }, []);

  // Tras una acción: estado de la cola y listas al día.
  const refreshAll = useCallback(() => {
    loadQueue();
    setTick((t) => t + 1);
  }, [loadQueue]);

  useEffect(() => {
    if (!isAllowed) return undefined;
    loadQueue();
    const id = setInterval(() => {
      loadQueue();
      setTick((t) => t + 1);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [isAllowed, loadQueue]);

  if (!isAllowed) {
    return (
      <Box p={8}>
        <Text color="gray.500">Esta vista está disponible solo para administradores.</Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 3, md: 6 }}>
      <Box borderRadius="16px" p={{ base: 4, md: 6 }} mb={4} color="white" bgGradient="linear(135deg, #6D48E5 0%, #8B5CF6 55%, #A855F7 100%)">
        <Flex align="center" gap={4} wrap="wrap">
          <Flex w="48px" h="48px" borderRadius="12px" bg="rgba(255,255,255,.15)" align="center" justify="center">
            <Icon as={FiCheckSquare} boxSize={6} />
          </Flex>
          <Box flex={1} minW="220px">
            <Text fontSize="11px" textTransform="uppercase" letterSpacing=".08em" opacity={0.85} fontWeight="600">
              Monitoring · calificación con IA
            </Text>
            <Heading fontSize="22px" fontWeight="700" letterSpacing="-.01em" mt="2px">
              Calificación de actividades
            </Heading>
          </Box>
          <QueueStatus
            queue={queue}
            onPausedSubjects={() => {
              setSubjectsToResume([]);
              setModal('subjects');
            }}
          />
          {queue && !queue.releasePausedByEnv && (
            <Button
              leftIcon={<Icon as={queue.releasePaused ? FiPlay : FiPause} />}
              onClick={() => setModal(queue.releasePaused ? 'resume' : 'pause')}
              variant="outline"
              color="white"
              borderColor="rgba(255,255,255,.55)"
              _hover={{ bg: 'rgba(255,255,255,.12)' }}
              borderRadius="10px"
              size="sm"
              h="38px"
            >
              {queue.releasePaused ? 'Reanudar publicación' : 'Pausar publicación'}
            </Button>
          )}
        </Flex>
      </Box>

      <Flex as="nav" role="tablist" aria-label="Secciones" gap={2} mb={5} borderBottom="1px solid" borderColor="gray.200">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <Box
              key={t.key}
              as="button"
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setSearchParams(t.key === 'entregas' ? {} : { tab: t.key })}
              px={1.5}
              py={3}
              mb="-1px"
              borderBottom="3px solid"
              borderColor={active ? '#574feb' : 'transparent'}
              color={active ? '#3730A3' : 'gray.600'}
              fontWeight={active ? '700' : '500'}
              fontSize="15px"
              _hover={active ? {} : { color: 'gray.800' }}
            >
              {t.label}
            </Box>
          );
        })}
      </Flex>

      {tab === 'entregas' && <SittingsTab tick={tick} queue={queue} onOpenSitting={setOpenSitting} />}
      {tab === 'historial' && <RunsTab tick={tick} onOpenSitting={setOpenSitting} onChanged={refreshAll} queue={queue} />}
      {tab === 'revision' && <PrechecksTab tick={tick} onOpenSitting={setOpenSitting} />}
      {tab === 'activacion' && <ActivationTab tick={tick} />}
      {tab === 'pruebas' && !searchParams.get('prueba') && (
        <TestsTab tick={tick} onOpenTest={(id) => setSearchParams({ tab: 'pruebas', prueba: String(id) })} />
      )}
      {tab === 'pruebas' && searchParams.get('prueba') && (
        <TestResult
          testId={Number(searchParams.get('prueba'))}
          onBack={() => setSearchParams({ tab: 'pruebas' })}
          onChanged={refreshAll}
        />
      )}

      <SittingDetailDrawer sittingId={openSitting} onClose={() => setOpenSitting(null)} onChanged={refreshAll} />

      <ConfirmActionModal
        isOpen={modal === 'pause'}
        onClose={() => setModal(null)}
        title="Pausar publicación"
        description="Ninguna nota se publica mientras esté en pausa. La calificación sigue corriendo y las notas quedan esperando; al reanudar se publican en las siguientes ejecuciones."
        confirmLabel="Pausar publicación"
        danger
        successMessage="Publicación en pausa"
        onConfirm={(reason) => pauseRelease(reason)}
        onDone={refreshAll}
      />
      <ConfirmActionModal
        isOpen={modal === 'resume'}
        onClose={() => setModal(null)}
        title="Reanudar publicación"
        description={`Las notas que ya cumplieron su hora se publican en la siguiente ejecución${queue?.releasePausedBy ? ` (la pausó ${queue.releasePausedBy.name})` : ''}.`}
        confirmLabel="Reanudar"
        withReason={false}
        successMessage="Publicación reanudada"
        onConfirm={() => resumeRelease()}
        onDone={refreshAll}
      />
      <ConfirmActionModal
        isOpen={modal === 'subjects'}
        onClose={() => setModal(null)}
        title="Materias en pausa"
        description="Sus entregas no se califican ni se publican. Marca las que quieres reactivar."
        confirmLabel={subjectsToResume.length ? `Reactivar ${subjectsToResume.length}` : 'Reactivar'}
        canConfirm={subjectsToResume.length > 0}
        withReason={false}
        successMessage="Materias reactivadas"
        onConfirm={() => resumeSubjects(subjectsToResume)}
        onDone={refreshAll}
      >
        <Stack spacing={2}>
          {(queue?.pausedSubjects || []).map((sub) => (
            <Checkbox
              key={sub.id}
              isChecked={subjectsToResume.includes(sub.id)}
              onChange={(e) => setSubjectsToResume((ids) => (e.target.checked ? [...ids, sub.id] : ids.filter((id) => id !== sub.id)))}
            >
              {sub.name}
            </Checkbox>
          ))}
        </Stack>
      </ConfirmActionModal>
    </Box>
  );
}
